/**
 * ReleaseToRetailPage — Releases tab
 * ====================================
 *
 * Catalogue, formats, channels and publishing. Priority insights from
 * the Command Center sit above the fold so the executive sees the
 * highest-urgency signals immediately. Supply-chain + full impact view
 * live inside the collapsible Network Overview in the layout chrome.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { API_BASE } from '../../../lib/apiBase';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Loader2, Radio, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useDistributionDemo } from './DistributionDemoContext';
import CommandCenter from './components/CommandCenter';
import PageSummary from './components/PageSummary';

const API = `${API_BASE}/api`;

export default function ReleaseToRetailPage() {
    const navigate = useNavigate();
    const { intel, sim } = useOutletContext() || {};
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [publishing, setPublishing] = useState(false);
    const {
        publishedChannels,
        setPublishedChannels,
        publishedAt,
        setPublishedAt,
    } = useDistributionDemo();

    useEffect(() => {
        axios.get(`${API}/admin/distribution-demo/release`)
            .then(res => {
                setData(res.data);
                setPublishedChannels(prev => {
                    if (Object.keys(prev).length > 0) return prev;
                    const seed = {};
                    (res.data.channels || []).forEach(ch => {
                        seed[ch.channel_id] = ch.connected;
                    });
                    return seed;
                });
            })
            .catch(err => {
                console.error('Failed to load release demo data', err);
                toast.error('Failed to load demo data');
            })
            .finally(() => setLoading(false));
    }, [setPublishedChannels]);

    const toggleChannel = (channelId) => {
        setPublishedChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
    };

    const handlePublish = () => {
        const selected = Object.entries(publishedChannels).filter(([, v]) => v).map(([k]) => k);
        if (selected.length === 0) {
            toast.error('Select at least one retail channel first');
            return;
        }
        setPublishing(true);
        setTimeout(() => {
            setPublishedAt(new Date().toISOString());
            setPublishing(false);
            toast.success(`Published to ${selected.length} channel${selected.length === 1 ? '' : 's'}`);
        }, 500);
    };

    const selectedCount = Object.values(publishedChannels).filter(Boolean).length;

    const hasOptionsBySku = useMemo(() => {
        // Supply gap insights have options via the Automation tab.
        return { 'OTR-001-VNL': true, 'OTR-001-CD': true };
    }, []);

    if (loading) {
        return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>;
    }
    if (!data) return null;

    const { release, channels } = data;

    const summaryMetrics = [
        {
            testid: 'kpi-release-status',
            label: 'Release status',
            value: publishedAt ? 'Published' : 'Ready to publish',
            accent: publishedAt ? 'text-emerald-700' : 'text-slate-900',
            hint: publishedAt ? new Date(publishedAt).toLocaleTimeString() : 'Awaiting your approval',
        },
        {
            testid: 'kpi-channels-synced',
            label: 'Channels synced',
            value: `${selectedCount} / ${channels.length}`,
            hint: channels.filter(c => publishedChannels[c.channel_id]).map(c => c.name).slice(0, 2).join(', ') || 'None selected',
        },
        {
            testid: 'kpi-variants',
            label: 'Variants',
            value: release.variants.length,
            hint: release.variants.map(v => v.sku.split('-').slice(-1)[0]).join(' · '),
        },
    ];

    return (
        <div className="space-y-5" data-testid="release-to-retail-page">
            <PageSummary metrics={summaryMetrics} />

            {intel && sim && (
                <CommandCenter
                    insights={intel.insights}
                    projections={sim.projections}
                    hasOptionsBySku={hasOptionsBySku}
                    onExploreGap={() => navigate('/admin/distribution/automation')}
                />
            )}

            {/* Release summary + channels (tighter layout) */}
            <Card className="overflow-hidden border-slate-200">
                <div className="grid md:grid-cols-[140px_1fr] gap-0">
                    <div className="aspect-square md:aspect-auto bg-gradient-to-br from-indigo-500 via-purple-600 to-pink-500 flex items-center justify-center">
                        <div className="text-white text-center p-3">
                            <div className="text-[10px] uppercase tracking-widest opacity-80">Volume</div>
                            <div className="text-4xl font-black mt-0.5">01</div>
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                                    {release.label} · {release.catalogue_number}
                                </div>
                                <h2 className="text-lg font-bold text-slate-900 mt-0.5">{release.title}</h2>
                                <div className="text-xs text-slate-600">
                                    {release.artist} · {release.genre} · {release.release_date}
                                </div>
                            </div>
                            <Button
                                onClick={handlePublish}
                                disabled={publishing || selectedCount === 0}
                                size="sm"
                                data-testid="publish-to-retail-button"
                            >
                                {publishing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Radio className="h-4 w-4 mr-1.5" />}
                                {publishedAt ? `Re-sync ${selectedCount}` : `Publish to ${selectedCount}`}
                            </Button>
                        </div>

                        <div className="mt-3 grid sm:grid-cols-3 gap-2">
                            {release.variants.map(v => (
                                <div key={v.sku} className="border border-slate-200 rounded-md p-2.5">
                                    <div className="text-[11px] text-slate-500">{v.sku}</div>
                                    <div className="text-xs font-semibold text-slate-900">{v.format}</div>
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        €{v.retail_price_eur.toFixed(2)} retail
                                        {v.on_hand !== null && <> · {v.on_hand.toLocaleString()} on hand</>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Card>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-900">Retail channels</h3>
                    <span className="text-xs text-slate-500">Syncs catalogue, stock and pricing</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                    {channels.map(ch => {
                        const on = !!publishedChannels[ch.channel_id];
                        return (
                            <Card
                                key={ch.channel_id}
                                className={`p-3 border transition-colors ${on ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`}
                                data-testid={`channel-card-${ch.channel_id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5 min-w-0">
                                        <div
                                            className="h-8 w-8 rounded-md flex-shrink-0 flex items-center justify-center text-white text-xs font-bold"
                                            style={{ backgroundColor: ch.logo_color }}
                                        >
                                            {ch.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-slate-900 truncate">{ch.name}</div>
                                            <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                                <Globe className="h-3 w-3" />
                                                {ch.type} · {ch.country}
                                            </div>
                                        </div>
                                    </div>
                                    <Switch
                                        checked={on}
                                        onCheckedChange={() => toggleChannel(ch.channel_id)}
                                        data-testid={`channel-toggle-${ch.channel_id}`}
                                    />
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
